import React from "react";
import { QueueEntryApi } from "../../services/queue/types";
import { patientDisplayName } from "../../services/queue/stats";
import VisitReasonBadge from "../VisitReason/VisitReasonBadge";
import { PatientAvatar, WaitTimeBadge } from "./QueueBits";
import { EntryActions, SecretaryEntryActions } from "./SecretaryQueueTable";

type DoctorColumn = { doctorId: number | null; doctorName: string; entries: QueueEntryApi[] };

/** Regroupe les entrées par médecin en gardant l'ordre FIFO dans chaque colonne. */
const groupByDoctor = (entries: QueueEntryApi[]): DoctorColumn[] => {
  const columns = new Map<number | null, DoctorColumn>();
  entries.forEach((entry) => {
    const column = columns.get(entry.assignedDoctorId) ?? {
      doctorId: entry.assignedDoctorId,
      doctorName: entry.assignedDoctorName ?? "Médecin non renseigné",
      entries: [],
    };
    column.entries.push(entry);
    columns.set(entry.assignedDoctorId, column);
  });
  return Array.from(columns.values());
};

/** Vue « Par médecin » : une colonne par médecin, chacune en ordre d'arrivée. */
const QueueByDoctor: React.FC<{ entries: QueueEntryApi[] } & SecretaryEntryActions> = ({
  entries,
  rankById,
  onEdit,
  onRemove,
}) => {
  const columns = groupByDoctor(entries);
  if (columns.length === 0) {
    return (
      <p className="rounded-2xl border border-slate-200 bg-white px-5 py-10 text-center text-sm text-slate-400">
        Aucun patient ne correspond aux filtres.
      </p>
    );
  }
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {columns.map((column) => (
        <section
          key={column.doctorId ?? "none"}
          aria-label={column.doctorName}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <header className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <i className="bx bx-plus-medical text-emerald-600" aria-hidden />
              {column.doctorName}
            </h3>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
              {column.entries.length}
            </span>
          </header>
          <ul>
            {column.entries.map((entry) => (
              <li key={entry.id} className="group/row flex items-center gap-3 border-t border-slate-100 px-5 py-3 first:border-t-0">
                <span className="w-5 text-center text-sm font-bold text-slate-400 tabular-nums">{rankById.get(entry.id)}</span>
                <PatientAvatar entry={entry} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">{patientDisplayName(entry)}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <VisitReasonBadge label={entry.visitReasonLabel} color={entry.visitReasonColor} />
                    <WaitTimeBadge minutes={entry.waitingMinutes} />
                  </div>
                </div>
                <EntryActions entry={entry} onEdit={onEdit} onRemove={onRemove} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};

export default QueueByDoctor;
